/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { CellChange, CellLocation, CheckboxCell, Column, DateCell, DefaultCellTypes, DropdownCell, NumberCell, TextCell } from "@silevis/reactgrid";
import { ImageCell } from "../template/ImageCellTemplate";
import { createErrorKey } from "./errorKey";

// Convert change - cell entity of reactgrid - to data entity
export const applyChangesToData = <T>(
    changes: CellChange<DefaultCellTypes | ImageCell>[],
    prevData: T[]
): T[] => {
    // Create a deep copy of prevData to avoid mutating the original data
    const newData = prevData.map(item => ({ ...item }));

    changes.forEach(({ rowId, columnId, type, newCell, previousCell }) => {
        const dataIndex = rowId as number;
        const fieldName = columnId as keyof T;

        switch (type) {
        case "text":
        case "email":
            newData[dataIndex][fieldName] = (newCell as TextCell).text as T[keyof T];
            break;
        case "date":
            newData[dataIndex][fieldName] = (newCell as DateCell).date as T[keyof T];
            break;
        case "number":
            newData[dataIndex][fieldName] = (newCell as NumberCell).value as T[keyof T];
            break;
        case "checkbox":
            newData[dataIndex][fieldName] = (newCell as CheckboxCell).checked as T[keyof T];
            break;
        case "dropdown":
            if (
            (newCell as DropdownCell).selectedValue !==
            (previousCell as DropdownCell).selectedValue
            ) {
            newData[dataIndex][fieldName] = (newCell as DropdownCell).selectedValue as T[keyof T];
            }
            (newData[dataIndex] as any).isOpen = (newCell as DropdownCell).isOpen as T[keyof T];
            break;
        default:
            break;
        }
    });

    return [...newData];
};

// Convert data to change - change is the cell entity of reactgrid
export const generateChangesFromData = <T,>(data: T[]) => {
    const changes: CellChange<DefaultCellTypes | ImageCell>[] = [];
  
    data.forEach((item: T, idx) => {
      Object.entries(item as object).forEach(([key, value]) => {
        let change: CellChange<DefaultCellTypes | ImageCell>;
  
        if (typeof value === "string") {
          change = {
            rowId: idx,
            columnId: key,
            type: "text",
            newCell: { text: value } as TextCell,
            previousCell: { text: value } as TextCell,
          };
        } else if (typeof value === "number") {
          change = {
            rowId: idx,
            columnId: key,
            type: "number",
            newCell: { value } as NumberCell,
            previousCell: { value } as NumberCell,
          };
        } else if (typeof value === "boolean") {
          change = {
            rowId: idx,
            columnId: key,
            type: "checkbox",
            newCell: { checked: value } as CheckboxCell,
            previousCell: { checked: value } as CheckboxCell,
          };
        } else if (value instanceof Date) {
          change = {
            rowId: idx,
            columnId: key,
            type: "date",
            newCell: { date: value } as DateCell,
            previousCell: { date: value } as DateCell,
          };
        } else if (Array.isArray(value)) {
          // Assuming dropdown options
          const options = value as string[];
          change = {
            rowId: idx,
            columnId: key,
            type: "dropdown",
            newCell: {
              type: "dropdown",
              selectedKey: options[0],
              options,
              values: options,
            } as unknown as DropdownCell,
            previousCell: {
              type: "dropdown",
              selectedKey: options[0],
              options,
              values: options,
            } as unknown as DropdownCell,
          };
        } else if (typeof value === "object" && value !== null) {
          // Serialize object as text
          const serializedObject = JSON.stringify(value);
          change = {
            rowId: idx,
            columnId: key,
            type: "text",
            newCell: { text: serializedObject } as TextCell,
            previousCell: { text: serializedObject } as TextCell,
          };
        } else {
          throw new Error(`Unsupported cell type for key: ${key}`);
        }
  
        changes.push(change);
      });
    });
  
    return changes;
};

export const setTargetCellError = (rowId: string, columnId: string, errorMessage: string) => ({
  [createErrorKey(rowId, columnId)]: errorMessage,
});

const createColumnMappings = (columns: Column[]) => {
  const idToIndex = new Map(columns.map((col, index) => [col.columnId, index]));
  const indexToId = new Map(columns.map((col, index) => [index, col.columnId.toString()]));
  return { idToIndex, indexToId };
};

// [{ rowId: '1', columnId: 'name' }] => [{ rowId: '1', columnId: 0 }]
export const errorCellsWithColumnIndices = (errorCells: CellLocation[], columns: Column[]) => {
  const { idToIndex } = createColumnMappings(columns);
  return errorCells.map(({ rowId, columnId }) => ({
    rowId,
    columnId: idToIndex.get(columnId) ?? 0,
  }));
};

// { rowId: '1', columnId: 'name' } => { rowId: '1', columnId: 0 }
export const focusCellWithColumnIndex = (cell: CellLocation, columns: Column[]) => {
  const { idToIndex } = createColumnMappings(columns);
  return {
    rowId: cell.rowId,
    columnId: idToIndex.get(cell.columnId) ?? 0,
  };
};

// { rowId: '1', columnId: 0 } => { rowId: '1', columnId: 'name' }
export const focusCellWithColumnId = (modifiedCell: CellLocation, columns: Column[]) => {
  const { indexToId } = createColumnMappings(columns);
  return {
    rowId: modifiedCell.rowId,
    columnId: indexToId.get(modifiedCell.columnId as number) ?? '',
  };
}; 